/**
 * Action Renderer Registry
 *
 * Central registry mapping action types to their rendering components
 * and metadata (title, icon, category).
 *
 * This registry enables:
 * - Single source of truth for action type handling
 * - Easy addition of new action types
 * - Consistent metadata across all rendering contexts
 */

import { Dataflow03, ShieldTick } from '@untitledui/icons';

// Import action renderer components
import { CalendarEventCard } from '../../../components/proposals/CalendarEventCard';
import { CreateActionCard } from '../../../components/proposals/CreateActionCard';
import { DeleteActionCard } from '../../../components/proposals/DeleteActionCard';
import { EmailCard } from '../../../components/proposals/EmailCard';
import { ExternalActionCard } from '../../../components/proposals/ExternalActionCard';
import { HiddenEntityCard } from '../../../components/proposals/HiddenEntityCard';
import { LinkActionCard } from '../../../components/proposals/LinkActionCard';
import { MergeActionCard } from '../../../components/proposals/MergeActionCard';
import { AskUserForInfoRenderer } from '../../../components/proposals/renderers/AskUserForInfoRenderer';
import { CreateAutomationRenderer } from '../../../components/proposals/renderers/CreateAutomationRenderer';
import { CreatePipelineRenderer } from '../../../components/proposals/renderers/CreatePipelineRenderer';
import { CreateValidationRuleRenderer } from '../../../components/proposals/renderers/CreateValidationRuleRenderer';
import { DealPipelineCustomizationRenderer } from '../../../components/proposals/renderers/DealPipelineCustomizationRenderer';
import { OnboardingActionRenderer } from '../../../components/proposals/renderers/OnboardingActionRenderer';
import { ReviewPermissionsRenderer } from '../../../components/proposals/renderers/ReviewPermissionsRenderer';
import { StageAssociationCard } from '../../../components/proposals/StageAssociationCard';
import { UpdateActionCard } from '../../../components/proposals/UpdateActionCard';
import type { ActionPayload, ProposedAction } from '../core/types';
import type { OnboardingActionPayload } from '../core/types';
import {
  isAskUserForInfoPayload,
  isCalendarEventCreatePayload,
  isCreateAutomationPayload,
  isCreateValidationRulePayload,
  isOnboardingActionPayload,
} from '../core/types';
// Import icons - using inline SVG components for consistency with CategoryIcon
import {
  AskUserForInfoIcon,
  CalendarIcon,
  CreateIcon,
  DeleteIcon,
  EmailIcon,
  ExternalLinkIcon,
  LinkIcon,
  LockIcon,
  MergeIcon,
  PipelineIcon,
  TeamIcon,
  UnlinkIcon,
} from './icons';
import {
  getAssociationAddedApproveLabel,
  getAssociationAddedTitle,
  getAssociationRemovedApproveLabel,
  getAssociationRemovedTitle,
  getCalendarTitle,
  getExternalActionTitle,
  getGmailTitle,
  getHiddenEntityAddApproveLabel,
  getHiddenEntityAddTitle,
  getObjectCreatedApproveLabel,
  getObjectCreatedTitle,
  getObjectDeletedApproveLabel,
  getObjectDeletedTitle,
  getObjectMergedApproveLabel,
  getObjectMergedTitle,
  getOnboardingActionTitle,
  getPipelineSuccessState,
  getPropertyUpdateApproveLabel,
  getPropertyUpdateTitle,
  getStageAssociationAddedApproveLabel,
  getStageAssociationAddedTitle,
  getStageAssociationRemovedApproveLabel,
  getStageAssociationRemovedTitle,
  renderAssociationCompact,
  renderObjectCreatedCompact,
  renderObjectDeletedCompact,
  renderPropertyUpdateCompact,
  renderStageAssociationCompact,
} from './registry.helpers';
import type { ActionRegistry } from './types';

// ============================================================================
// Registry Definition
// ============================================================================

/**
 * The central Action Registry.
 *
 * Maps action type keys to their components and metadata.
 * All action types that can be rendered should be registered here.
 */
export const ACTION_REGISTRY: ActionRegistry = {
  // Gmail Send Action
  gmail_send: {
    component: EmailCard,
    getTitle: getGmailTitle,
    category: 'email',
    icon: EmailIcon,
    supportsEditing: true,
    isDestructive: false,
    badgeLabel: 'Email',
    colorScheme: 'default',
    getApproveLabel: () => 'Send email',
  },

  // Calendar Event Create Action
  calendar_event_create: {
    component: CalendarEventCard,
    getTitle: getCalendarTitle,
    category: 'calendar',
    icon: CalendarIcon,
    supportsEditing: true,
    isDestructive: false,
    badgeLabel: 'Calendar',
    colorScheme: 'default',
    getApproveLabel: (payload, action, context) => {
      void action;
      if (!isCalendarEventCreatePayload(payload)) {
        return 'Create & send invite';
      }
      const attendees = payload.action_params.attendees ?? [];
      const currentUserEmail = context?.currentUserEmail?.trim().toLowerCase();
      // A "real" invitee is any attendee that is not the current user.
      // When the current user's email is unavailable, we cannot tell self
      // from others, so any non-empty attendee list counts as inviting.
      const hasRealInvitee = attendees.some(email => {
        if (!currentUserEmail) {
          return true;
        }
        return email.trim().toLowerCase() !== currentUserEmail;
      });
      return hasRealInvitee ? 'Create & send invite' : 'Create event';
    },
  },

  // External Action
  external_action: {
    component: ExternalActionCard,
    getTitle: getExternalActionTitle,
    category: 'external_action',
    icon: ExternalLinkIcon,
    supportsEditing: false,
    isDestructive: false,
    badgeLabel: 'External',
    colorScheme: 'default',
  },

  // Onboarding Action
  onboarding_action: {
    component: OnboardingActionRenderer,
    getTitle: getOnboardingActionTitle,
    category: 'onboarding',
    icon: TeamIcon,
    supportsEditing: true,
    isDestructive: false,
    badgeLabel: 'Onboarding',
    colorScheme: 'default',
    getApproveLabel: (payload: ActionPayload, action: ProposedAction) => {
      // Use action parameter to satisfy registry type signature
      void action;
      if (isOnboardingActionPayload(payload)) {
        const params = (payload as OnboardingActionPayload).action_params;
        const count =
          params.selected_emails?.length ?? params.suggestions?.length ?? 0;
        if (count === 0) {
          return 'Continue without inviting';
        }
        return `Send invites to ${count} ${count === 1 ? 'person' : 'people'}`;
      }
      return 'Send invitations';
    },
  },

  // Timeline Event: Property Updated
  'timeline_event:ahoy_property_updated': {
    component: UpdateActionCard,
    getTitle: getPropertyUpdateTitle,
    category: 'pipeline_update',
    icon: PipelineIcon,
    supportsEditing: true,
    isDestructive: false,
    badgeLabel: 'Update',
    colorScheme: 'default',
    renderCompact: renderPropertyUpdateCompact,
    getApproveLabel: getPropertyUpdateApproveLabel,
  },

  // Timeline Event: Object Created
  'timeline_event:ahoy_object_created': {
    component: CreateActionCard,
    getTitle: getObjectCreatedTitle,
    category: 'crm_create',
    icon: CreateIcon,
    supportsEditing: true,
    isDestructive: false,
    badgeLabel: 'Create',
    colorScheme: 'default',
    renderCompact: renderObjectCreatedCompact,
    getApproveLabel: getObjectCreatedApproveLabel,
  },

  // Timeline Event: Object Deleted
  'timeline_event:ahoy_object_deleted': {
    component: DeleteActionCard,
    getTitle: getObjectDeletedTitle,
    category: 'crm_delete',
    icon: DeleteIcon,
    supportsEditing: false,
    isDestructive: true,
    badgeLabel: 'Delete',
    colorScheme: 'destructive',
    renderCompact: renderObjectDeletedCompact,
    getApproveLabel: getObjectDeletedApproveLabel,
  },

  // Timeline Event: Association Added
  'timeline_event:ahoy_association_added': {
    component: LinkActionCard,
    getTitle: getAssociationAddedTitle,
    category: 'crm_associate',
    icon: LinkIcon,
    supportsEditing: false,
    isDestructive: false,
    badgeLabel: 'Link',
    colorScheme: 'default',
    renderCompact: renderAssociationCompact,
    getApproveLabel: getAssociationAddedApproveLabel,
  },

  // Timeline Event: Association Removed
  'timeline_event:ahoy_association_removed': {
    component: LinkActionCard,
    getTitle: getAssociationRemovedTitle,
    category: 'crm_associate',
    icon: UnlinkIcon,
    supportsEditing: false,
    isDestructive: false,
    badgeLabel: 'Unlink',
    colorScheme: 'warning',
    renderCompact: renderAssociationCompact,
    getApproveLabel: getAssociationRemovedApproveLabel,
  },

  // Frontend-only sub-route: stage associations are ahoy_association_added events
  // in the backend, but getActionKey() detects stage association types in the
  // payload and routes them here instead of to the generic LinkActionCard.
  // This is NOT a real CRM event type — it's a synthetic registry key.
  'timeline_event:stage_association_added': {
    component: StageAssociationCard,
    getTitle: getStageAssociationAddedTitle,
    category: 'pipeline_update',
    icon: PipelineIcon,
    supportsEditing: true,
    isDestructive: false,
    badgeLabel: 'Stage',
    colorScheme: 'default',
    renderCompact: renderStageAssociationCompact,
    getApproveLabel: getStageAssociationAddedApproveLabel,
  },

  // Frontend-only sub-route: same as above, for ahoy_association_removed events
  // targeting stage association types.
  'timeline_event:stage_association_removed': {
    component: StageAssociationCard,
    getTitle: getStageAssociationRemovedTitle,
    category: 'pipeline_update',
    icon: PipelineIcon,
    supportsEditing: false,
    isDestructive: false,
    badgeLabel: 'Stage',
    colorScheme: 'warning',
    renderCompact: renderStageAssociationCompact,
    getApproveLabel: getStageAssociationRemovedApproveLabel,
  },

  // Synthetic key — actions targeting hidden entities (entities that exist
  // in the CRM but are not visible to users: org-user projections,
  // tombstoned external persons, inbound stubs). getActionKey() routes any
  // timeline_event with enriched_data.is_hidden here, as a degraded-render
  // safety net: the viewer's user-scoped store can't hydrate the entity, so
  // HiddenEntityCard renders from enriched_data instead of a normal card.
  // The original producer (Gmail/Calendar auto-synced contacts awaiting an
  // "Add to CRM" acknowledgment) was removed with inbound contact removal —
  // approving one of these executes the underlying action only; nothing is
  // unhidden.
  //
  // `supportsEditing: true` because stage associations on hidden entities
  // support inline editing of the target stage (see HiddenEntityCard's
  // edit mode). Per-action gating in `isEditableAction` restricts the
  // edit affordance to the stage-association subcase.
  'timeline_event:hidden_entity_add': {
    component: HiddenEntityCard,
    getTitle: getHiddenEntityAddTitle,
    category: 'pipeline_update',
    icon: PipelineIcon,
    supportsEditing: true,
    isDestructive: false,
    badgeLabel: 'Update',
    colorScheme: 'default',
    getApproveLabel: getHiddenEntityAddApproveLabel,
  },

  // Timeline Event: Object Merged
  'timeline_event:ahoy_object_merged': {
    component: MergeActionCard,
    getTitle: getObjectMergedTitle,
    category: 'crm_merge',
    icon: MergeIcon,
    supportsEditing: false,
    isDestructive: true,
    badgeLabel: 'Merge',
    colorScheme: 'destructive',
    getApproveLabel: getObjectMergedApproveLabel,
  },

  // Ask User For Info Action
  ask_user_for_info: {
    component: AskUserForInfoRenderer,
    getTitle: payload =>
      isAskUserForInfoPayload(payload)
        ? `Answer ${payload.fields.length} question${payload.fields.length === 1 ? '' : 's'}`
        : 'Answer questions',
    category: 'crm_create',
    icon: AskUserForInfoIcon,
    supportsEditing: true,
    isDestructive: false,
    badgeLabel: 'Info',
    colorScheme: 'default',
    getApproveLabel: () => 'Submit',
  },

  // Create Validation Rule (standalone action type)
  create_validation_rule: {
    component: CreateValidationRuleRenderer,
    getTitle: payload =>
      isCreateValidationRulePayload(payload)
        ? payload.display_name
        : 'Create validation rule',
    category: 'crm_create',
    icon: ShieldTick,
    supportsEditing: false,
    isDestructive: false,
    badgeLabel: 'Rule',
    colorScheme: 'default',
    getApproveLabel: () => 'Create rule',
  },

  // Create Pipeline (standalone action type; surfaces in the onboarding panel
  // via the handler's "pipeline" filter tag)
  create_pipeline: {
    component: CreatePipelineRenderer,
    getTitle: () => 'Create pipeline',
    category: 'pipeline_update',
    icon: PipelineIcon,
    supportsEditing: true,
    isDestructive: false,
    badgeLabel: 'Pipeline',
    colorScheme: 'default',
    // "Create", not "Save" — approval executes the proposal (creates the
    // pipeline, fields, and relationships); inline edits already autosave.
    getApproveLabel: () => 'Create pipeline',
    getSuccessState: (_payload, _action, orgId) =>
      getPipelineSuccessState(orgId),
  },

  // Create Automation (read-only in v1; approve/reject only)
  create_automation: {
    component: CreateAutomationRenderer,
    getTitle: payload =>
      isCreateAutomationPayload(payload) && payload.display_name
        ? payload.display_name
        : 'Create automation',
    category: 'automation',
    icon: Dataflow03,
    supportsEditing: false,
    isDestructive: false,
    badgeLabel: 'Automation',
    colorScheme: 'default',
    getApproveLabel: () => 'Create automation',
  },

  // Onboarding Action: Customize Pipeline
  'onboarding_action:customize_pipeline': {
    component: DealPipelineCustomizationRenderer,
    getTitle: () => 'Customize pipeline',
    category: 'pipeline_update',
    icon: PipelineIcon,
    supportsEditing: true,
    isDestructive: false,
    badgeLabel: 'Pipeline',
    colorScheme: 'default',
    getApproveLabel: () => 'Save pipeline',
    getSuccessState: (_payload, _action, orgId) =>
      getPipelineSuccessState(orgId),
  },

  // Onboarding Action: Review Permissions
  'onboarding_action:review_permissions': {
    component: ReviewPermissionsRenderer,
    getTitle: () => 'Review permissions',
    category: 'onboarding',
    icon: LockIcon,
    supportsEditing: true,
    isDestructive: false,
    badgeLabel: 'Permissions',
    colorScheme: 'default',
    getApproveLabel: () => 'Save preferences',
    getSuccessState: (_payload, _action, orgId) => ({
      title: 'Your preferences have been saved.',
      description:
        'You can update your visibility settings anytime in your profile settings.',
      secondaryButton: {
        label: 'View profile settings',
        getPath: () => `/${orgId}/settings/integrations`,
      },
    }),
  },
};
